import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock3, Plus, ShieldX, Umbrella } from 'lucide-react';
import api from '../utils/api';
import './CoachCalendar.css';

const STORAGE_KEY = 'mskica-coach-calendar-draft-v1';
const DAY_START = 6;
const DAY_END = 22;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FALLBACK_LANES = [
  { id: 1, name: "Kapil Dev's Den" },
  { id: 3, name: "MS Dhoni's Den" },
  { id: 5, name: "Rahul Dravid's Den" },
  { id: 2, name: "Sachin Tendulkar's Den" },
  { id: 4, name: "Virat Kohli's Den" },
  { id: 13, name: "VVS Laxman's Den" }
];

const initialSchedule = {
  hours: [{ id: 'hours-default', days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00', effectiveFrom: '2026-01-01', effectiveUntil: '', laneId: '', note: '' }],
  oof: [{ id: 'oof-default', date: '2026-09-16', start: '12:30', end: '14:30', allDay: false, note: 'Personal appointment' }],
  block: [{ id: 'block-default', date: '2026-09-18', start: '16:00', end: '18:00', laneId: 4, note: 'Private coaching setup' }]
};

const startOfWeek = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() - result.getDay());
  return result;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const isoDate = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const formatTime = (time) => new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const loadSchedule = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || initialSchedule;
  } catch {
    return initialSchedule;
  }
};

const CoachCalendar = () => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [schedule, setSchedule] = useState(loadSchedule);
  const [lanes, setLanes] = useState(FALLBACK_LANES);
  const [laneStatus, setLaneStatus] = useState('Loading lanes');
  const [laneFilter, setLaneFilter] = useState('all');
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    const loadLanes = async () => {
      try {
        const response = await api.get('/api/public/lane-booking');
        const offers = response.data?.lanes || response.data?.services || response.data || [];
        const mapped = offers.map((lane) => ({ id: lane.item_id || lane.id, name: lane.lane_name || lane.name })).filter((lane) => lane.id && lane.name);
        if (mapped.length) {
          setLanes(mapped);
          setLaneStatus('Lane services synced');
          return;
        }
        setLaneStatus('Using verified lane snapshot');
      } catch {
        setLaneStatus('Using verified lane snapshot');
      }
    };
    loadLanes();
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(schedule));
  }, [schedule]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const weekEnd = weekDays[6];
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const startLabel = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endLabel = weekEnd.toLocaleDateString('en-US', sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
  const weekLabel = `${startLabel} - ${endLabel}, ${weekEnd.getFullYear()}`;

  const laneName = (laneId) => lanes.find((lane) => String(lane.id) === String(laneId))?.name || 'All lanes';

  const eventsForDate = (date) => {
    const dateString = isoDate(date);
    const events = [];
    schedule.hours.forEach((entry) => {
      const withinRange = dateString >= entry.effectiveFrom && (!entry.effectiveUntil || dateString <= entry.effectiveUntil);
      if (entry.days.includes(date.getDay()) && withinRange && (laneFilter === 'all' || !entry.laneId || String(entry.laneId) === laneFilter)) {
        events.push({ ...entry, type: 'hours', title: 'Working hours' });
      }
    });
    schedule.oof.filter((entry) => entry.date === dateString).forEach((entry) => events.push({ ...entry, type: 'oof', title: 'Out of office', start: entry.allDay ? '06:00' : entry.start, end: entry.allDay ? '22:00' : entry.end }));
    schedule.block.filter((entry) => entry.date === dateString && (laneFilter === 'all' || String(entry.laneId) === laneFilter)).forEach((entry) => events.push({ ...entry, type: 'block', title: 'Lane blocked' }));
    return events.sort((first, second) => first.start.localeCompare(second.start));
  };

  const totals = useMemo(() => {
    const working = schedule.hours.reduce((sum, entry) => sum + ((timeToMinutes(entry.end) - timeToMinutes(entry.start)) / 60) * entry.days.length, 0);
    const away = schedule.oof.reduce((sum, entry) => sum + (entry.allDay ? 16 : (timeToMinutes(entry.end) - timeToMinutes(entry.start)) / 60), 0);
    return { working, away, blocks: schedule.block.length };
  }, [schedule]);

  const openEditor = (type, event = null) => {
    const date = isoDate(weekStart);
    setEditor(event ? { ...event, originalType: event.type } : {
      id: '', type, originalType: type, date, effectiveFrom: date, effectiveUntil: '', days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00', allDay: false, laneId: '', note: ''
    });
  };

  const saveEditor = (event) => {
    event.preventDefault();
    const type = editor.type;
    const item = { ...editor, id: editor.id || `${type}-${Date.now()}` };
    delete item.type;
    delete item.originalType;
    setSchedule((current) => ({ ...current, [type]: editor.id ? current[type].map((entry) => entry.id === editor.id ? item : entry) : [...current[type], item] }));
    setEditor(null);
  };

  const deleteEditor = () => {
    setSchedule((current) => ({ ...current, [editor.type]: current[editor.type].filter((entry) => entry.id !== editor.id) }));
    setEditor(null);
  };

  const updateEditor = (field, value) => setEditor((current) => ({ ...current, [field]: value }));

  return (
    <section className="coach-calendar">
      <div className="calendar-intro">
        <div>
          <span className="calendar-eyebrow">My schedule</span>
          <h2>Lane &amp; working hours</h2>
          <p>Set when you coach, mark time away, and reserve lanes before players book them.</p>
        </div>
        <div className="calendar-actions">
          <button type="button" className="calendar-button" onClick={() => openEditor('hours')}><Clock3 size={16} /> Working hours</button>
          <button type="button" className="calendar-button" onClick={() => openEditor('oof')}><Umbrella size={16} /> OOF</button>
          <button type="button" className="calendar-button primary" onClick={() => openEditor('block')}><ShieldX size={16} /> Block lane</button>
        </div>
      </div>

      <div className="calendar-stats">
        <div><span>Working hours</span><strong>{totals.working}h</strong></div>
        <div><span>Time away</span><strong>{totals.away}h</strong></div>
        <div><span>Lane blocks</span><strong>{totals.blocks}</strong></div>
        <div><span>Schedule status</span><strong className="published"><i /> Published</strong></div>
      </div>

      <div className="calendar-surface">
        <div className="calendar-toolbar">
          <div className="week-controls">
            <button type="button" aria-label="Previous week" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</button>
            <button type="button" aria-label="Next week" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight size={18} /></button>
            <strong>{weekLabel}</strong>
          </div>
          <div className="calendar-filters">
            <label>Jump to<input type="date" value={isoDate(weekStart)} onChange={(event) => setWeekStart(startOfWeek(new Date(`${event.target.value}T12:00:00`)))} /></label>
            <label>Lane<select value={laneFilter} onChange={(event) => setLaneFilter(event.target.value)}><option value="all">All lanes</option>{lanes.map((lane) => <option key={lane.id} value={lane.id}>{lane.name}</option>)}</select></label>
          </div>
        </div>
        <div className="calendar-legend">
          <span><i className="work" /> Working hours</span><span><i className="away" /> Out of office</span><span><i className="blocked" /> Lane blocked</span><small>{laneStatus}</small>
        </div>

        <div className="calendar-grid-wrap">
          <div className="calendar-grid">
            <div className="calendar-corner" />
            {weekDays.map((date) => <div className="calendar-day-head" key={isoDate(date)}><span>{DAYS[date.getDay()]}</span><strong className={isoDate(date) === isoDate(new Date()) ? 'today' : ''}>{date.getDate()}</strong></div>)}
            <div className="calendar-time-axis">{Array.from({ length: DAY_END - DAY_START + 1 }, (_, index) => <span key={index} style={{ top: index * 48 }}>{formatTime(`${String(DAY_START + index).padStart(2, '0')}:00`)}</span>)}</div>
            {weekDays.map((date) => <div className="calendar-day-track" key={`track-${isoDate(date)}`}>{eventsForDate(date).map((entry) => {
              const top = ((timeToMinutes(entry.start) - DAY_START * 60) / 60) * 48;
              const height = Math.max(((timeToMinutes(entry.end) - timeToMinutes(entry.start)) / 60) * 48, 28);
              return <button type="button" key={entry.id} className={`calendar-event ${entry.type}`} style={{ top, height }} onClick={() => openEditor(entry.type, entry)}><strong>{entry.title}</strong><span>{formatTime(entry.start)} - {formatTime(entry.end)}</span>{entry.type === 'block' && <small>{laneName(entry.laneId)}</small>}</button>;
            })}</div>)}
          </div>
        </div>

        <div className="calendar-agenda">{weekDays.map((date) => <div className="agenda-day" key={`agenda-${isoDate(date)}`}><h3>{date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</h3>{eventsForDate(date).length ? eventsForDate(date).map((entry) => <button type="button" key={entry.id} onClick={() => openEditor(entry.type, entry)}><time>{formatTime(entry.start)}</time><span><strong>{entry.title}</strong><small>{entry.type === 'block' ? laneName(entry.laneId) : entry.note || 'Available'}</small></span></button>) : <p>No schedule entries</p>}</div>)}</div>
      </div>

      {editor && <div className="schedule-modal" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setEditor(null)}><form className="schedule-dialog" onSubmit={saveEditor}>
        <header><div><span>{editor.type === 'hours' ? 'Availability' : editor.type === 'oof' ? 'Time away' : 'Lane access'}</span><h3>{editor.id ? 'Edit' : 'Add'} {editor.type === 'hours' ? 'working hours' : editor.type === 'oof' ? 'out of office' : 'lane block'}</h3></div><button type="button" aria-label="Close" onClick={() => setEditor(null)}>×</button></header>
        <div className="schedule-form">
          {editor.type === 'hours' ? <><label className="full">Days<div className="day-picks">{DAYS.map((day, index) => <button type="button" className={editor.days.includes(index) ? 'selected' : ''} key={day} onClick={() => updateEditor('days', editor.days.includes(index) ? editor.days.filter((value) => value !== index) : [...editor.days, index])}>{day.slice(0, 1)}</button>)}</div></label><label>Effective from<input type="date" required value={editor.effectiveFrom} onChange={(event) => updateEditor('effectiveFrom', event.target.value)} /></label><label>Effective until<input type="date" value={editor.effectiveUntil} onChange={(event) => updateEditor('effectiveUntil', event.target.value)} /></label></> : <label className="full">Date<input type="date" required value={editor.date} onChange={(event) => updateEditor('date', event.target.value)} /></label>}
          {editor.type === 'oof' && <label className="check full"><input type="checkbox" checked={editor.allDay} onChange={(event) => updateEditor('allDay', event.target.checked)} /> All day</label>}
          {!editor.allDay && <><label>Starts<input type="time" required value={editor.start} onChange={(event) => updateEditor('start', event.target.value)} /></label><label>Ends<input type="time" required min={editor.start} value={editor.end} onChange={(event) => updateEditor('end', event.target.value)} /></label></>}
          {editor.type !== 'oof' && <label className="full">Lane<select required={editor.type === 'block'} value={editor.laneId} onChange={(event) => updateEditor('laneId', event.target.value)}><option value="">{editor.type === 'hours' ? 'All lanes' : 'Select a lane'}</option>{lanes.map((lane) => <option key={lane.id} value={lane.id}>{lane.name}</option>)}</select></label>}
          <label className="full">Note<textarea value={editor.note} onChange={(event) => updateEditor('note', event.target.value)} placeholder="Optional details" /></label>
        </div>
        <footer>{editor.id ? <button type="button" className="delete" onClick={deleteEditor}>Delete</button> : <span />}<div><button type="button" onClick={() => setEditor(null)}>Cancel</button><button type="submit" className="save"><Plus size={15} /> Save</button></div></footer>
      </form></div>}
    </section>
  );
};

export default CoachCalendar;